import React from 'react';
import { Document, Page, Text, View, Image, StyleSheet, Font } from '@react-pdf/renderer';

interface Prize {
  place: string;
  duration: string;
}

interface PrizesPDFProps {
  tournament: string;
  prizes: Prize[];
  title: string;
  subtitle: string;
  placeColumn: string;
  durationColumn: string;
  footerText: string;
  worthText: string;
  monthsText: string;
}

// Register Baloo 2 font
Font.register({
  family: 'Baloo 2',
  fonts: [
    {
      src: 'https://fonts.gstatic.com/s/baloo2/v23/wXK0E3kTposypRydzVT08TS3JnAmtdgazapv.ttf',
      fontWeight: 400,
    },
    {
      src: 'https://fonts.gstatic.com/s/baloo2/v23/wXK0E3kTposypRydzVT08TS3JnAmtdj9yqpv.ttf',
      fontWeight: 700,
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    fontFamily: 'Baloo 2',
    paddingHorizontal: 40,
    paddingVertical: 35,
    display: 'flex',
    flexDirection: 'column',
  },
  // App icon at top
  appIconSection: {
    alignItems: 'center',
    marginBottom: 16,
  },
  appIcon: {
    width: 100,
    height: 100,
    borderRadius: 20,
  },
  // Header section
  header: {
    alignItems: 'center',
    marginBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: '#1C7AD6',
    textAlign: 'center',
    marginBottom: 14,
    letterSpacing: 1,
  },
  subtitle: {
    fontSize: 13,
    color: '#1e293b',
    textAlign: 'center',
    marginBottom: 12,
    fontWeight: 700,
  },
  tournament: {
    fontSize: 11,
    color: '#64748b',
    textAlign: 'center',
    lineHeight: 1.7,
  },
  // Divider
  divider: {
    width: '25%',
    height: 1.5,
    backgroundColor: '#e2e8f0',
    marginVertical: 20,
    alignSelf: 'center',
  },
  // Info section (before prizes)
  infoSection: {
    alignItems: 'center',
    marginBottom: 16,
  },
  infoText: {
    fontSize: 10,
    color: '#64748b',
    textAlign: 'center',
    lineHeight: 1.7,
    marginBottom: 12,
    paddingHorizontal: 30,
  },
  signatureContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  signatureImage: {
    width: 80,
    height: 40,
  },
  // Prizes list
  prizesList: {
    marginTop: 10,
    marginBottom: 20,
  },
  prizeItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    border: '2px solid #e2e8f0',
    borderRadius: 14,
    padding: 12,
    marginBottom: 10,
  },
  prizeItemWrapper: {
    breakInside: 'avoid',
    pageBreakInside: 'avoid',
  },
  trophyContainer: {
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  trophyIcon: {
    width: 45,
    height: 45,
  },
  prizeContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  placeText: {
    fontSize: 13,
    color: '#1e293b',
    fontWeight: 700,
    flex: 1,
  },
  durationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 12,
  },
  durationText: {
    fontSize: 16,
    fontWeight: 700,
    color: '#ff9f10',
    marginRight: 6,
  },
  // Pro badge (yellow background, white text) - matching certificate style
  proBadge: {
    backgroundColor: '#ff9f10',
    borderRadius: 12,
    paddingVertical: 4,
    paddingHorizontal: 10,
    marginRight: 12,
  },
  proBadgeText: {
    fontSize: 9,
    color: '#ffffff',
    fontWeight: 700,
  },
  worthText: {
    fontSize: 11,
    color: '#64748b',
    minWidth: 80,
    textAlign: 'right',
  },
  // Separator after info
  separator: {
    width: '100%',
    height: 1.5,
    backgroundColor: '#e2e8f0',
    marginBottom: 20,
  },
  // Footer
  footer: {
    marginTop: 'auto',
    paddingTop: 16,
    alignItems: 'center',
  },
  // Page number
  pageNumber: {
    position: 'absolute',
    fontSize: 9,
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: '#94a3b8',
  },
});

const PrizesPDF: React.FC<PrizesPDFProps> = ({
  tournament,
  prizes,
  title,
  subtitle,
  placeColumn,
  durationColumn,
  footerText,
  worthText,
  monthsText,
}) => {
  const appIconUrl = `${window.location.origin}/app-icon.png`;
  const trophyUrl = `${window.location.origin}/trophy.png`;
  const signatureUrl = `${window.location.origin}/doruk-signature.png`;

  return (
    <Document>
      <Page size="A4" orientation="portrait" style={styles.page}>
        {/* App Icon */}
        <View style={styles.appIconSection}>
          <Image src={appIconUrl} style={styles.appIcon} />
        </View>

        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.subtitle}>{subtitle}</Text>
          <Text style={styles.tournament}>{tournament}</Text>
        </View>

        {/* Info Section with text and signature */}
        <View style={styles.infoSection}>
          <Text style={styles.infoText}>{footerText}</Text>
          <View style={styles.signatureContainer}>
            <Image src={signatureUrl} style={styles.signatureImage} />
          </View>
        </View>

        {/* Separator */}
        <View style={styles.separator} />

        {/* Prizes List */}
        <View style={styles.prizesList}>
          {prizes.map((prize, index) => {
            const worthValue = parseInt(prize.duration) * 5;
            return (
              <View key={index} style={styles.prizeItemWrapper} wrap={false}>
                <View style={styles.prizeItem}>
                  {/* Trophy Icon */}
                  <View style={styles.trophyContainer}>
                    <Image src={trophyUrl} style={styles.trophyIcon} />
                  </View>

                  {/* Prize Content */}
                  <View style={styles.prizeContent}>
                    {/* Place */}
                    <Text style={styles.placeText}>{prize.place}</Text>

                    {/* Duration */}
                    <View style={styles.durationContainer}>
                      <Text style={styles.durationText}>
                        {prize.duration} {monthsText}
                      </Text>
                    </View>

                    {/* Pro Badge */}
                    <View style={styles.proBadge}>
                      <Text style={styles.proBadgeText}>PRO</Text>
                    </View>

                    {/* Worth */}
                    <Text style={styles.worthText}>
                      {worthText} CHF {worthValue}.-
                    </Text>
                  </View>
                </View>
              </View>
            );
          })}
        </View>

        {/* Footer */}
        <View style={styles.footer} />

        {/* Page Number */}
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
};

export default PrizesPDF;
